import { Box, LinearProgress, Paper, Typography } from "@mui/material"
import { AddOutlined } from "@mui-symbols-material/w300"
import { useNavigate, useParams } from "@tanstack/react-router"
import type { FC } from "react"
import { useState } from "react"

import { ProductEquivalentGroupModal } from "./components/ProductEquivalentGroupModal"
import { ProductEquivalentGroupsTable } from "./components/ProductEquivalentGroupsTable"

import { InformationMissingAlert } from "@/components/common/Alerts/InformationMissingAlert"
import { Button } from "@/components/common/Button"
import { PermissionActionType } from "@/constants/permissions"
import { type EquivalencyGroup, EquivalencyGroupSortField, SortingOrder } from "@/graphql/codegen/graphql"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import useEquivalentProductGroups from "@/screens/ManageProducts/components/EquivalentProductGroups/hooks/useEquivalentProductGroups"

export const ProductEquivalentGroupsContainer: FC = () => {
  const { productId: productIdParam } = useParams({
    from: "/app/product/$productId/edit",
  })
  const productId = Number(productIdParam)

  const navigate = useNavigate()
  const { hasPermissions } = usePermissionManager()

  const [selectedGroup, setSelectedGroup] = useState<EquivalencyGroup | null>(null)

  const {
    groupsList: groups,
    groupsListFetching: fetching,
    refetch,
  } = useEquivalentProductGroups({
    productIds: [productId],
  })

  const hasEquivalentPermission = hasPermissions([PermissionActionType.EQUIVALENT_MAINTENANCE])

  const handleNavigateToManage = () => {
    navigate({
      to: "/app/admin/manage-products/equivalents",
      search: {
        field: EquivalencyGroupSortField.Name,
        order: SortingOrder.Asc,
      },
    })
  }

  const handleViewGroup = (group: EquivalencyGroup) => {
    setSelectedGroup(group)
  }

  const handleCloseModal = () => {
    setSelectedGroup(null)
  }

  const handleSuccess = () => {
    refetch()
  }

  return (
    <Paper
      className='flex flex-1 flex-col border border-gray-300 p-6'
      elevation={0}
      data-testid='product-equivalent-groups-container'
    >
      <Typography variant='h6' className='mb-6'>
        Equivalent Product Groups
      </Typography>

      {fetching && (
        <Box className='py-4'>
          <LinearProgress />
        </Box>
      )}

      {!fetching && groups.length === 0 && (
        <InformationMissingAlert title='This product is not in any equivalent groups' data-testid='no-groups-alert'>
          {hasEquivalentPermission && (
            <Box className='mt-2'>
              <Button
                color='inherit'
                size='small'
                appearance='outlined'
                className='text-primary'
                startIcon={<AddOutlined />}
                onClick={handleNavigateToManage}
                data-testid='manage-equivalent-groups-button'
              >
                Manage Equivalent Groups
              </Button>
            </Box>
          )}
        </InformationMissingAlert>
      )}

      {!fetching && groups.length > 0 && <ProductEquivalentGroupsTable groups={groups} onViewGroup={handleViewGroup} />}

      {selectedGroup && (
        <ProductEquivalentGroupModal
          group={selectedGroup}
          currentProductId={productId}
          onClose={handleCloseModal}
          onSuccess={handleSuccess}
        />
      )}
    </Paper>
  )
}
